import React from 'react';
import { MailCheck, RotateCw } from 'lucide-react';
import { Card, EmptyState, IconButton, Pill, SectionHeader, Skeleton } from '../../../components/ui';
import { NOTIFICATION_LABELS } from '../../../types/notifications';
import { timeAgo } from '../../monitoring/format';
import { STATUS_LABEL, STATUS_REASON, statusTone } from '../format';
import { useHistory } from '../useNotifications';
import { Notice } from './ui';

/**
 * The answer to "why did I get this" and "why did I not": what was sent, when,
 * and whether it arrived — with the server's own reason when it did not.
 */
export const NotificationHistory: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { entries, loading, error, unavailable, reload } = useHistory();

  return (
    <section className={className}>
      <SectionHeader
        title="Recently sent"
        aside={
          !unavailable && (
            <IconButton label="Refresh" size="xs" onClick={reload}>
              <RotateCw className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          )
        }
      />

      {unavailable ? (
        <Card className="p-3">
          <Notice tone="quiet">Email notifications are not available on this server yet.</Notice>
        </Card>
      ) : (
        <Card divided>
          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="px-4 py-3">
                <Skeleton className="h-4 w-64" />
                <Skeleton className="mt-2 h-3 w-40" />
              </div>
            ))
          ) : error ? (
            <div className="p-3">
              <Notice tone="error">{error}</Notice>
            </div>
          ) : entries.length === 0 ? (
            <EmptyState icon={MailCheck} title="Nothing sent to you yet" />
          ) : (
            entries.map((entry) => {
              const reason = entry.detail || STATUS_REASON[entry.status];
              return (
                <div key={entry.id} className="flex items-start gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] text-ink">{entry.subject}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-neutral-400">
                      <span>{NOTIFICATION_LABELS[entry.kind]?.title ?? entry.kind}</span>
                      <span aria-hidden="true">·</span>
                      <span title={new Date(entry.createdAt).toLocaleString()}>{timeAgo(entry.createdAt)}</span>
                    </p>
                    {entry.status !== 'sent' && reason && (
                      <p className="mt-1 text-[12px] text-neutral-500">{reason}</p>
                    )}
                  </div>
                  <Pill tone={statusTone(entry.status)} className="flex-shrink-0">
                    {STATUS_LABEL[entry.status]}
                  </Pill>
                </div>
              );
            })
          )}
        </Card>
      )}
    </section>
  );
};
